import mitt from 'mitt';

/**
 * Event types for PixiJS ↔ React communication
 */
export type GameEventTypes = {
  // PixiJS → React: Player state updates
  playerStateUpdate: {
    hull: number;
    maxHull: number;
    shield: number;
    maxShield: number;
    credits: number;
    x: number;
    y: number;
    rotation: number;
    speed: number;
    zoom: number;
    totalPower: number;
    // Weapon heat system
    weaponHeat: number;
    weaponMaxHeat: number;
    isOverheated: boolean;
    hasWeapon: boolean;
    // Missile ammo system
    ammoCount: number;
    magazineSize: number;
    isReloading: boolean;
    reloadProgress: number;
    isMissileWeapon: boolean;
    // Boost system
    boostEnergy: number;
    boostMaxEnergy: number;
    isBoosting: boolean;
    hasBooster: boolean;
    // Ship info for status panel
    shipName: string;
    shipClassType: string;
    shipTier: number;
    // Activity status
    activityStatus: 'idle' | 'moving' | 'mining' | 'combat' | 'docking' | 'warping' | 'stunned';
    // Shield drag
    isShieldDraining: boolean;
    // Combat-overhaul: ship-level heat & natural shield (optional during migration)
    shipHeat?: number;
    shipMaxHeat?: number;
    isShipOverheated?: boolean;
    shieldActive?: boolean;
    // Hard flux: voluntary vent + frozen-heat tracker
    isVenting?: boolean;
    absorbedHeat?: number;
    powerCapacity?: number;
    // Active weapon info for HUD indicator
    activeWeaponName?: string;
    activeWeaponType?: string;
    activeWeaponIndex?: number;
    equippedWeaponCount?: number;
  };

  // PixiJS → React: View mode changes based on zoom
  viewModeChange: 'flight' | 'constellation' | 'universe';

  // PixiJS → React: Target selection changed
  targetChanged: {
    id: string;
    type: 'player' | 'asteroid' | 'station' | 'drop' | 'drone' | null;
    name?: string;
    x?: number;
    y?: number;
  } | null;

  // PixiJS → React: Docking state changed
  dockingStateChange: {
    isDocked: boolean;
    stationId?: string;
    stationName?: string;
  };

  // PixiJS → React: Near station - show dock prompt
  dockPrompt: {
    show: boolean;
    stationName?: string;
  };

  // PixiJS → React: Near gateway - show travel prompt
  gatewayPrompt: {
    show: boolean;
    targetConstellationName?: string;
    travelCost?: number;
    gatewayId?: string;
  };

  // PixiJS → React: Near planet - show "Open Planet Panel" prompt
  planetLandingPrompt: {
    show: boolean;
    planetName?: string;
    planetId?: string;
  };

  // PixiJS → React: Open the planet management panel (player stays in space; ship
  // becomes invulnerable while panel is open).
  openPlanetPanel: {
    planetId: string;
  };

  // React → PixiJS: Close the planet management panel (panel-driven).
  closePlanetPanel: void;

  // ==================== PLANET PANEL SERVER MESSAGES ====================
  // Forwarded from NetworkManager → React (via gameEvents emitter).

  /** Initial snapshot returned after `open_planet_panel` is accepted. */
  planetPanelSnapshot: unknown;
  /** Server rejected a panel action. */
  planetPanelError: { reason: string };
  /** Server confirmed close. */
  planetPanelClosed: Record<string, never>;
  /** A new structure appeared on this planet (own or other player's). */
  planetStructurePlanted: { structure: unknown; miningStats?: unknown };
  /** A structure was removed (manual or auto-dismantle). */
  planetStructureDismantled: { structureId: string; reason: 'manual' | 'auto_dismantle' };
  /** A mining station became inactive. */
  planetStructureDepleted: { structureId: string };
  /** A mining station was toggled on/off by its owner. */
  planetStructureToggled: { structureId: string; isActive: boolean };
  /** Outpost buffer changed (rate-limited from the mining tick). */
  planetBufferUpdated: { outpostId: string; contents: Record<string, number> };
  /** New activity log entry for the live feed. */
  planetActivityEntry: { entry: unknown };
  /** Detailed scan result for a (lat, lon) point. */
  planetScanDetails: unknown;
  /** Player's credit balance changed (after plant / dismantle / claim). */
  planetPanelCredits: { credits: number };
  /** Legacy depletion diffs (per-km-cell). Empty in Phase 3+ — superseded by planetHexStateUpdated. */
  planetRichnessDiffsUpdated: { diffs: Array<{ cell_lat: number; cell_lon: number; depletion_organic: number; depletion_crystal: number }> };
  /** Updated per-hex signed delta from the mining tick (Phase 3+ live heatmap). */
  planetHexStateUpdated: { hexState: Array<{ hex_id: number; delta_organic: number; delta_crystal: number }> };

  // PixiJS → React: Near asteroid - show mining prompt
  miningPrompt: {
    show: boolean;
    resourceType?: string;
    /** toolTier − resourceTier; 0 = matched, negative = mining slower than baseline. */
    tierAdvantage?: number;
    /** Resource tier (1..4); used by the HUD to label expected slowdown. */
    resourceTier?: number;
    /** Current tool tier; 0 means no tool equipped (still allowed to mine, just abysmal). */
    equippedTier?: number;
  };

  // PixiJS → React: Mining tool state (for HUD Mine button)
  miningToolInfo: {
    toolRange: number;    // 0 = no tool equipped
    toolTier: number;
    isMining: boolean;
    miningAsteroidId: string | null;
    miningTargetIds: string[];  // All active mining target IDs (multi-target)
    miningTargetCount: number;
    sessionResources: Record<string, number>; // resourceType → total mined this session
    sessionStartTime: number; // Date.now() when session began (0 = no session)
  };

  // React → PixiJS: Player requests to start mining an entity (asteroid, ice, gas, wreckage)
  requestStartMining: { asteroidId: string; kind?: string };

  // React → PixiJS: Player requests to stop mining. If asteroidId is provided,
  // only that target is stopped; otherwise all active mining is stopped.
  requestStopMining: { asteroidId?: string } | undefined;

  // PixiJS → React: Near loot drop - show pickup prompt
  dropPrompt: {
    show: boolean;
    dropId?: string;
    ownerOnly?: boolean;   // true if drop is still in 60s killer-protection window
    canAccess?: boolean;   // false if owner-only and viewer isn't owner
  };

  // PixiJS → React: open the loot transfer modal for a specific drop
  openDropModal: {
    dropId: string;
    items: Array<{ itemType: 'credits' | 'resource' | 'equipment'; itemId: string; quantity: number }>;
  };

  // PixiJS → React: drop inventory mutated (modal refreshes)
  dropInventoryUpdate: {
    dropId: string;
    items: Array<{ itemType: 'credits' | 'resource' | 'equipment'; itemId: string; quantity: number }>;
  };

  // PixiJS → React: drop was removed (modal closes if open)
  dropRemoved: {
    dropId: string;
  };

  // PixiJS → React: toggle the capital-ship drone-bay overlay (triggered by [B] in flight)
  toggleShipBay: void;

  // PixiJS → React: Gateway transition overlay
  gatewayTransition: {
    show: boolean;
    targetConstellationName?: string;
  };

  // PixiJS → React: Server-side ship cargo changed (mining tick added a unit,
  // loot picked up, drone delivery, etc.). Listeners re-fetch their view of
  // the cargo. Payload is intentionally generic — callers usually re-query.
  cargoChanged: { reason: 'mining' | 'loot' | 'other' };

  // PixiJS → React: Show notification
  notification: {
    type: 'info' | 'success' | 'warning' | 'error';
    message: string;
    duration?: number;
  };

  // PixiJS → React: Game ready (assets loaded, scene created)
  gameReady: void;

  // PixiJS → React: Loading progress
  loadingProgress: {
    progress: number;
    message: string;
  };

  // PixiJS → React: Constellation info
  constellationInfo: {
    name: string;
    tier: number;
    threatLevel: 'green' | 'yellow' | 'red';
  };

  // PixiJS → React: Current terrain zones the player is inside
  playerZones: {
    zones: Array<{ kind: string; name?: string }>;
  };

  // PixiJS → React: Loot pickup notification
  lootPickup: {
    credits: number;
    items: string[];
  };

  // PixiJS → React: Cargo update
  cargoUpdate: {
    items: Array<{
      resourceType: string;
      quantity: number;
    }>;
    capacity: number;
    used: number;
  };

  // PixiJS → React: Equipment update
  equipmentUpdate: {
    weapons: Array<{
      slot: number;
      type: string;
      name: string;
    }>;
    utilities: Array<{
      slot: number;
      type: string;
      name: string;
    }>;
  };

  // React → PixiJS: Toggle autopilot
  toggleAutopilot: void;

  // React → PixiJS: Set autopilot destination
  setDestination: {
    x: number;
    y: number;
    type?: 'station' | 'gate' | 'point';
    id?: string;
  };

  // React → PixiJS: Undock from station
  undock: void;

  // Player → GameScene: Fire projectile weapon
  fireWeapon: {
    x: number;
    y: number;
    angle: number;
    weapon: import('@astra/shared').Weapon;
    playerId: string;
    shipVelocityX: number;
    shipVelocityY: number;
    targetId?: string;
    targetType?: 'bot' | 'player' | 'asteroid' | 'drone';
  };

  // React → PixiJS: Toggle mining laser
  toggleMiningLaser: void;

  // React → PixiJS: Open/close inventory
  toggleInventory: void;

  // React → PixiJS: Zoom to level
  setZoom: number;

  // React → PixiJS: Camera shake (e.g., from UI action)
  cameraShake: {
    duration: number;
    intensity: number;
  };

  // Chat events
  chatMessage: {
    id: string;
    playerId: string;
    playerName: string;
    channel: 'global' | 'local' | 'party';
    message: string;
    timestamp: number;
    isSystem?: boolean;
  };

  // React → NetworkManager: Send chat message
  sendChatMessage: {
    channel: 'global' | 'local' | 'party';
    message: string;
  };

  // Toggle chat panel
  toggleChat: void;

  // Toggle equipment panel
  toggleEquipment: void;

  // PixiJS → React: Nearby entities update (for overview panel)
  nearbyEntitiesUpdate: Array<{
    id: string;
    type: 'player' | 'asteroid' | 'station' | 'drop' | 'gateway' | 'planet' | 'sun' | 'nebula' | 'drone' | 'settlement' | 'ice_asteroid' | 'gas_pocket' | 'wreckage';
    name: string;
    distance: number;
    x: number;
    y: number;
    data?: Record<string, unknown>;
    // Ship-specific fields (for players)
    shipName?: string;
    shipClassType?: string;
    shipTier?: number;
    shipSize?: string;
    totalPower?: number;
    hull?: number;
    maxHull?: number;
    shield?: number;
    maxShield?: number;
  }>;

  // PixiJS → React: Open constellation map via zoom
  // Tutorial v2: server pushes the current step → React renders the panel
  // when the player is in proximity of the in-world Aura Beacon. `mission`
  // tells the panel which mission-status badge to show, if any.
  tutorialState: {
    step: number;
    title: string;
    body: string;
    totalSteps: number;
    mission: 'mine' | 'kill' | null;
  };
  // Tutorial v2: server rejected a Continue because the mission isn't done.
  // Client shows a hint/toast and keeps the panel on the current step.
  tutorialMissionIncomplete: { step: number; mission: 'mine' | 'kill' };
  // Tutorial v2: live mission progress, pushed by the server ~1Hz while on a
  // mission step. Drives the disabled state of the Continue button.
  tutorialMissionProgress: { step: number; mission: 'mine' | 'kill'; progress: number; threshold: number; complete: boolean };
  // Post-tutorial: one-shot Aura "welcome to live space" transmission. Server
  // pushes once on the first non-tutorial constellation join after the Academy is
  // complete; client acks when the player closes the modal.
  welcomeToLiveSpace: Record<string, never>;
  // Tutorial: server confirms completion (last step Continue, Skip, or gateway out)
  tutorialComplete: Record<string, never>;
  // Tutorial v2: player is inside/outside the Aura Beacon radius. Drives the
  // panel's visibility — fired by GameScene's per-frame proximity check.
  tutorialBeaconRange: { inRange: boolean };

  openConstellationMap: {
    constellationName: string;
    // Per-constellation physical size in world units (FULL size, mirrors WORLD_CONFIG.
    // CONSTELLATION_SIZE / GameState.constellationSize). Drives the map's grid, boundary,
    // and scale calculations — tutorial constellations render at their real 50,000-
    // unit extent instead of the default 900,000.
    constellationSize: number;
    playerPosition: { x: number; y: number };
    playerRotation: number;
    planets: Array<{ id: string; name: string; systemId: string; x: number; y: number; radius: number; planetType: string; orbitalDistance?: number; hasPlayerOutpost?: boolean }>;
    stations: Array<{ id: string; name: string; x: number; y: number }>;
    asteroidZones?: Array<{ id: string; x: number; y: number; radius: number; name?: string }>;
    gateways: Array<{ id: string; name: string; x: number; y: number; targetConstellationName: string }>;
    systems: Array<{ id: string; name: string; x: number; y: number; starType: string; sunRadius: number }>;
    privateStations?: Array<{ id: string; name: string; x: number; y: number; isOwned: boolean }>;
    waypoint?: { x: number; y: number; name: string } | null;
    // V1 harvestables — entities within client AOI, shown as map markers
    harvestables?: Array<{
      id: string;
      kind: 'ice_asteroid' | 'gas_pocket' | 'wreckage';
      x: number;
      y: number;
      resourceType: string;
    }>;
    // Debug: resource zone hotspot locations for constellation map overlay
    resourceZones?: Array<{ id: string; x: number; y: number; radius: number; kind: string }>;
    // Player-owned drones (ship + private station) — always visible on constellation map
    ownedDrones?: Array<{ id: string; droneType: string; x: number; y: number; state: string; level?: number }>;
    // Tutorial v2 Aura beacons — proximity ring + label on the constellation map.
    tutorialBeacons?: Array<{ id: string; name: string; x: number; y: number; radius: number }>;
  };

  // React → PixiJS: Set waypoint from map click
  setWaypoint: {
    x: number;
    y: number;
    name?: string;
  };

  // React → PixiJS: Clear waypoint
  clearWaypoint: void;
  // Combat-overhaul: HUD asks GameScene to cycle active weapon on the player
  cycleActiveWeapon: void;

  // PixiJS → React: Waypoint update (for compass indicator)
  waypointUpdate: {
    x: number;
    y: number;
    name: string;
    distance: number;
    angle: number; // Angle from player to waypoint in radians
    isOnScreen: boolean; // Whether waypoint is visible on screen
  } | null;

  // React → PixiJS: Start approach to current waypoint or selected entity
  startFastTravel: void;

  // React → PixiJS: Start approach to a specific target (overrides waypoint priority)
  startFastTravelToTarget: {
    x: number;
    y: number;
    name: string;
  };

  // React → PixiJS: Stop approach
  stopFastTravel: void;

  // PixiJS → React: Approach status update
  fastTravelUpdate: {
    isActive: boolean;
    targetX?: number;
    targetY?: number;
    targetName?: string;
    distanceRemaining?: number;
    estimatedTimeSeconds?: number;
  };

  // React → PixiJS: Start follow on a selected entity (continuously tracks moving target)
  startFollow: {
    id: string;
    type: 'player' | 'asteroid' | 'station' | 'drop' | 'gateway' | 'planet' | 'sun' | 'nebula' | 'drone' | 'settlement' | 'ice_asteroid' | 'gas_pocket' | 'wreckage';
    name: string;
    distance?: number; // user-set clearance from target edge (defaults to last value)
  };

  // React → PixiJS: Update follow clearance distance while following
  setFollowDistance: { distance: number };

  // React → PixiJS: Stop follow
  stopFollow: void;

  // Player (key press) → GameScene: toggle follow on the currently selected
  // entity. GameScene resolves the target — Player doesn't know about selection.
  toggleFollow: void;

  // PixiJS → React: Follow status update
  followUpdate: {
    isActive: boolean;
    targetId?: string;
    targetName?: string;
    distanceRemaining?: number;
    followMode?: 'chasing' | 'idle' | 'retreating';
  };

  // PixiJS → React: Player died (Green/Yellow zones - ship kept)
  playerDied: {
    killerName?: string;
    killerType?: 'player' | 'environment';
    nearestStationName: string;
    nearestStationId: string;
    threatLevel: 'green' | 'yellow' | 'red';
    itemsDropped: string[];
  };

  // PixiJS → React: Ship lost in Red zone (ship deleted, get free starter)
  shipLost: {
    lostShipClass: string;
    nearestStationName: string;
    nearestStationId: string;
    itemsDropped: string[];
  };

  // React → PixiJS: Player requests respawn
  requestRespawn: void;

  // PixiJS → React: Player respawned
  playerRespawned: void;

  // PixiJS → React: Selection changed (from right-click in world)
  selectionChanged: {
    id: string;
    type: 'player' | 'asteroid' | 'station' | 'drop' | 'gateway' | 'planet' | 'sun' | 'nebula' | 'drone' | 'settlement' | 'ice_asteroid' | 'gas_pocket' | 'wreckage' | 'empty';
    name: string;
    x: number;
    y: number;
  } | null;

  // React → PixiJS: Select entity by ID (from Overview click)
  selectEntity: {
    id: string;
    type: 'player' | 'station' | 'gateway';
  };

  // ==================== WARP TRAVEL EVENTS ====================

  // PixiJS → React: Warp readiness changed (sustained movement toward waypoint)
  warpReadyChanged: {
    ready: boolean;
    targetX?: number;
    targetY?: number;
    targetName?: string;
  };

  // PixiJS → React: Warp warming up progress (during approach before ready)
  warpWarmingUp: {
    progress: number;  // 0-1 progress toward warp ready
    remainingMs: number;  // Milliseconds until warp ready
  };

  // React → PixiJS: Request to engage warp
  warpEngage: void;

  // React → PixiJS: Request to cancel warp
  warpCancel: void;

  // PixiJS → React: Warp state changed (from server)
  warpStateChanged: {
    phase: 'none' | 'ready' | 'calculating' | 'traveling' | 'cooldown';
    targetX?: number;
    targetY?: number;
    targetName?: string;
    warpSpeed?: number;
    calculationStartTime?: number;
    calculationDuration?: number;
    calculationEndTime?: number;
    calculationProgress?: number;  // 0-1 for progress bar
    cooldownUntil?: number;
    cooldownRemaining?: number;
    interruptReason?: string;
    distance?: number;             // Distance remaining to target
    estimatedTimeSeconds?: number; // ETA in seconds
  };

  // PixiJS → React: Warp was interrupted/interdicted
  warpInterrupted: {
    reason: string;
    beaconOwnerName?: string;
  };

  // ==================== GATEWAY CLOAK EVENTS ====================

  // PixiJS → React: Cloak state update (from server)
  cloakStateUpdate: {
    isCloaked: boolean;
    expiresAt: number; // Timestamp when cloak expires
    remaining: number; // Remaining time in ms
  };

  // PixiJS → React: Cloak was broken
  cloakBroken: {
    reason: 'expired' | 'fired' | 'collision';
  };

  // ==================== COMBAT STATE EVENTS ====================

  // PixiJS → React: Combat state update (from server)
  combatStateUpdate: {
    isInCombat: boolean;
    expiresAt: number; // Timestamp when combat state expires
    remaining: number; // Remaining time in ms
  };

  // ==================== REPUTATION EVENTS ====================

  // Server → React: Initial reputation sync on join
  reputationSync: {
    reputation: number;
    status: 'neutral' | 'wanted' | 'outlaw' | 'hero';
  };

  // Server → React: Reputation changed (from kill, etc)
  reputationChange: {
    oldReputation: number;
    newReputation: number;
    change: number;
    status: 'neutral' | 'wanted' | 'outlaw' | 'hero';
    victimX?: number;  // Position of killed target for floating indicator
    victimY?: number;
  };

  // Server → React: Police warning message
  policeWarning: {
    message: string;
  };

  // Server → React: Radiation damage from sun (bypasses shields)
  radiationDamage: {
    proximity: number;  // 0-1, how close to sun center (1 = lethal)
    damage: number;     // DPS being applied to hull
  };

  // PixiJS → React: Continuous radiation proximity check (for warning before damage)
  radiationProximity: {
    proximity: number;  // 0-1, how close to sun center (1 = lethal)
    damage: number;     // Estimated DPS at current distance
  } | null;

  // PixiJS → React: Reputation state from PlayerState (persists across scene changes)
  reputationStateUpdate: {
    reputation: number;
  };

  // ==================== BOUNTY SYSTEM EVENTS ====================

  // Server → React: Bounty list update (periodic broadcast every 5 minutes)
  bountyListUpdate: {
    bounties: import('@astra/shared').BountyInfo[];
    timestamp: number;
  };

  // Server → React: Bounty claimed notification (sent to hunter)
  bountyClaimed: {
    amount: number;
    repGain: number;
    targetName: string;
  };

  // Server → React: Bounty cleared (target killed or forgiven)
  bountyCleared: {
    playerId: string;
    playerName: string;
  };

  // React → PixiJS: Toggle bounty board modal
  toggleBountyBoard: void;

  // ==================== PLAYER → GAMESCENE EVENTS ====================
  // These events are emitted by Player.ts and handled by GameScene.ts

  // Player → GameScene: Start laser fire (continuous beam weapon)
  startLaserFire: {
    weapon: import('@astra/shared').Weapon;
    playerId: string;
  };

  // Player → GameScene: Stop laser fire
  stopLaserFire: void;

  // Player → GameScene: Selection click (right-click in world)
  selectionClick: {
    worldX: number;
    worldY: number;
    screenX: number;
    screenY: number;
  };

  // Player → GameScene: Player took damage (for autopilot interrupt)
  playerDamage: {
    hull: number;
    maxHull: number;
    shield: number;
    maxShield: number;
    damage: number;
  };

  // Player → GameScene: Player died (ship destroyed)
  playerDeath: void;

  // Player → GameScene: Player respawned
  playerRespawn: {
    hull: number;
    maxHull: number;
    shield: number;
    maxShield: number;
  };

  // Player → GameScene: Fast travel started (autopilot engaged)
  fastTravelStarted: {
    targetX: number;
    targetY: number;
  };

  // Player → GameScene: Fast travel stopped
  fastTravelStopped: {
    reason: 'arrival' | 'combat' | 'manual';
  };

  // UI → Player: Key bindings changed (player needs to rebuild key map)
  bindingsChanged: void;

  // ==================== RADAR SCANNING EVENTS ====================

  // PixiJS → React: Radar scan progress update
  radarScanUpdate: {
    isScanning: boolean;
    hasRadar: boolean;
    currentRange: number;    // Current scan radius in units
    maxRange: number;        // Max possible range for equipped radar
    scanProgress: number;    // 0-1 progress toward max range
    holdTime: number;        // Seconds held so far
    scanComplete: boolean;   // True when scan reached max range
    isCharging: boolean;     // True during cooldown period
    chargeRemaining: number; // Seconds remaining in cooldown
    validScan?: boolean;     // True when scan passed first time threshold (yields results)
  };

  // PixiJS → React: Clear scanned entities (new scan started, death, docking, etc.)
  radarClearEntities: void;

  // Player → GameScene: Request radar scan from server (throttled)
  radarScanRequest: {
    x: number;
    y: number;
    range: number;
  };

  // Server → Client: Entities discovered by radar scan
  radarEntitiesDiscovered: {
    entities: Array<{
      type: 'hotspot' | 'asteroid' | 'planet' | 'nebula' | 'drone' | 'player' | 'station' | 'private_station' | 'gateway' | 'sun' | 'ice_asteroid' | 'gas_pocket' | 'wreckage' | 'drop' | 'beacon';
      id: string;
      name: string;
      x: number;
      y: number;
      distance: number;
      radius?: number;
      resourceType?: string;
      planetType?: string;
      droneType?: string;
      starType?: string;
      targetConstellationName?: string;
      ownerName?: string;
      shipClassId?: string;
    }>;
  };

  // PixiJS → React: Player entered/exited a nebula (debuff zone)
  nebulaStateChange: {
    isInNebula: boolean;
    nebulaName: string;
  };

  // React → PixiJS: Request fresh constellation map data (M key press)
  requestConstellationMapData: void;

  // Request current scanned entities (e.g., when constellation map opens)
  requestScannedEntities: Record<string, never>; // Empty object

  // Response with current scanned entities
  requestScannedEntitiesResponse: {
    entities: Array<{
      type: 'hotspot' | 'planet' | 'nebula' | 'drone' | 'player' | 'station' | 'private_station' | 'gateway' | 'sun' | 'ice_asteroid' | 'gas_pocket' | 'wreckage' | 'drop' | 'beacon';
      id: string;
      name: string;
      x: number;
      y: number;
      distance: number;
      radius?: number;
      resourceType?: string;
      droneType?: string;
      starType?: string;
      targetConstellationName?: string;
      ownerName?: string;
      shipClassId?: string;
    }>;
  };

  // Clear scanned entities from overview panel
  clearOverviewScans: Record<string, never>; // Empty object

  // PixiJS → React: Planet orbital position update (for constellation map real-time orbits)
  planetOrbitalUpdate: { id: string; x: number; y: number };

  // DEV: Dummy enemy count changed
  dummyCountChanged: { count: number };

  // ==================== UTILITY HOTBAR EVENTS ====================

  // PixiJS → React: Utility hotbar state update (equipped utilities + cooldowns)
  utilityHotbarUpdate: {
    slots: Array<{
      slotIndex: number;
      equipped: boolean;
      utilityType: string;
      utilityName: string;
      utilityId: string;
      tier: number;
      isActive: boolean;
      isOnCooldown: boolean;
      cooldownPercent: number;
      cooldownRemaining: number;
      isPassive: boolean;
      energyPercent?: number;
    } | null>;
  };

  // React → PixiJS: Activate a utility hotbar slot (keys 1-5)
  activateUtilitySlot: number;  // slot index 0-4

  // Player → GameScene: request inspect with live-position range check
  requestInspectTarget: { targetId: string };

  // GameScene → HUD: close the inspect panel
  inspectClose: void;

  // Server → Client: Inspect target result (detailed scan of selected player/asteroid)
  inspectResult: {
    success: boolean;
    error?: string;
    targetId?: string;
    targetName?: string;
    shipClass?: string;
    shipName?: string;
    hull?: number;
    maxHull?: number;
    reputation?: number;
    equipment?: Array<{
      slotType: string;
      slotSize: string;
      itemId: string;
      itemName: string;
      tier: number;
      category: string;
    }>;
    cargo?: {
      capacity: number;
      used: number;
      items: Array<{ itemType: 'resource' | 'equipment'; itemId: string; quantity: number; volume: number }>;
    };
    entityType?: string;
    resourceType?: string;
    resourceAmount?: number;
    resourceTier?: number;
    miningTimeEstimate?: number;
  };

  // Server → Client: Cargo scan result
  cargoScanResult: {
    success: boolean;
    error?: string;
    targetId?: string;
    targetName?: string;
    shipClass?: string;
    cargo?: {
      capacity: number;
      used: number;
      items: Array<{ itemType: 'resource' | 'equipment'; itemId: string; quantity: number; volume: number }>;
    };
  };

  // Server → Client: Warp disruptor activated (start visual effect)
  disruptorActivated: {
    duration: number;
    cooldown: number;
    range: number;
  };

  // Server → Client: anti-cheat movement sanity check rejected the last input.
  // Local player must snap to the authoritative position the server holds.
  positionCorrection: {
    x: number;
    y: number;
    rotation: number;
    velocityX: number;
    velocityY: number;
    reason: string;
  };
};

/**
 * Global event emitter for cross-system communication
 */
export const gameEvents = mitt<GameEventTypes>();

/**
 * Helper to emit events with type safety
 */
export function emitGameEvent<K extends keyof GameEventTypes>(
  event: K,
  payload?: GameEventTypes[K]
): void {
  // Use type assertion to work around mitt's strict typing
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (gameEvents.emit as any)(event, payload);
}

/**
 * Helper to subscribe to events with type safety
 */
export function onGameEvent<K extends keyof GameEventTypes>(
  event: K,
  handler: (payload: GameEventTypes[K]) => void
): () => void {
  gameEvents.on(event, handler as (payload: unknown) => void);
  return () => gameEvents.off(event, handler as (payload: unknown) => void);
}
